import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Box, Typography, IconButton, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { Close as CloseIcon, Download as DownloadIcon, Print as PrintIcon } from "@mui/icons-material";

interface VehicleQRCodeProps {
  plate: string;
  brand?: string;
  model?: string;
  open: boolean;
  onClose: () => void;
}

const VehicleQRCode: React.FC<VehicleQRCodeProps> = ({ plate, brand, model, open, onClose }) => {
  // Le QR contient l'URL pour ouvrir directement l'affectation
  const qrValue = `${window.location.origin}/assignments/new?plate=${encodeURIComponent(plate)}`;
  
  const handleDownload = () => {
    const svg = document.getElementById("vehicle-qr-code");
    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        const pngFile = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.download = `QR-${plate}.png`;
        downloadLink.href = pngFile;
        downloadLink.click();
      };
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "", "width=400,height=500");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Code - ${plate}</title>
            <style>
              body { 
                display: flex; 
                flex-direction: column; 
                align-items: center; 
                justify-content: center; 
                min-height: 100vh;
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 20px;
                box-sizing: border-box;
              }
              .qr-container {
                text-align: center;
                padding: 30px;
                border: 3px solid #333;
                border-radius: 15px;
              }
              .plate {
                font-size: 32px;
                font-weight: bold;
                margin-bottom: 15px;
                padding: 10px 20px;
                background: #f0f0f0;
                border-radius: 8px;
                letter-spacing: 2px;
              }
              .vehicle-info {
                font-size: 14px;
                color: #666;
                margin-bottom: 15px;
              }
              svg {
                max-width: 200px;
              }
              .instructions {
                font-size: 12px;
                color: #888;
                margin-top: 15px;
              }
            </style>
          </head>
          <body>
            <div class="qr-container">
              <div class="plate">${plate}</div>
              ${brand && model ? `<div class="vehicle-info">${brand} ${model}</div>` : ""}
              ${document.getElementById("vehicle-qr-code")?.outerHTML || ""}
              <div class="instructions">Scanner pour démarrer une affectation</div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" fontWeight="bold">
          QR Code Véhicule
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 3,
            gap: 2,
          }}
        >
          {/* Plaque */}
          <Box
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: "grey.100",
              borderRadius: 2,
              border: "2px solid",
              borderColor: "grey.300",
            }}
          >
            <Typography variant="h5" fontWeight="bold" letterSpacing={2}>
              {plate}
            </Typography>
          </Box>
          
          {/* Info véhicule */}
          {brand && model && (
            <Typography variant="body2" color="text.secondary">
              {brand} {model}
            </Typography>
          )}
          
          {/* QR Code */}
          <Box
            sx={{
              p: 3,
              bgcolor: "white",
              borderRadius: 2,
              boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            }}
          >
            <QRCodeSVG
              id="vehicle-qr-code"
              value={qrValue}
              size={180}
              level="H"
              includeMargin
            />
          </Box>
          
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Scanner ce QR code pour démarrer<br />une affectation avec ce véhicule
          </Typography>
          
          {/* Actions */}
          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            <IconButton
              onClick={handleDownload}
              sx={{
                bgcolor: "primary.light",
                color: "primary.main",
                "&:hover": { bgcolor: "primary.main", color: "white" },
              }}
              title="Télécharger"
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={handlePrint}
              sx={{
                bgcolor: "secondary.light",
                color: "secondary.main",
                "&:hover": { bgcolor: "secondary.main", color: "white" },
              }}
              title="Imprimer"
            >
              <PrintIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VehicleQRCode;

